import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Key,
  ClipboardCheck,
  FileQuestion,
  PauseCircle,
  CalendarDays,
  ArrowRight,
  ExternalLink,
  RefreshCw,
} from 'lucide-react';
import { supabase } from '../../supabaseClient';
import PendingOrdersWidget from '../shared/PendingOrdersWidget';
import SearchAnalyticsWidget from '../shared/SearchAnalyticsWidget';
import BackorderManagementCard from './BackorderManagementCard';
import ChangelogWidget from '../shared/ChangelogWidget';
import ChangelogModal from '../shared/ChangelogModal';
import OrderSyncButton from './OrderSyncButton';
import JobHistory from './JobHistory';
import Card from '../shared/Card';
import PageHeader from '../shared/PageHeader';
import Button from '../shared/Button';
import {
  useOrderOverview,
  StatCardGrid,
  RecentOrdersCard,
  OrdersChartCard,
  LiveIndicator,
} from '../shared/OrderOverview';

const AdminDashboard = () => {
  const history = useHistory();
  const { stats, recentOrders, statusCounts, timeline, loading, realtimeConnected, refresh } =
    useOrderOverview('admin-dashboard');
  const [apiStats, setApiStats] = useState(null);
  const [showChangelog, setShowChangelog] = useState(false);

  useEffect(() => {
    fetchApiStats();
  }, []);

  const fetchApiStats = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/v1/admin/stats/overview`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setApiStats({
          totalApiKeys: data.stats.total_api_keys || 0,
          activeApiKeys: data.stats.active_api_keys || 0,
          totalAccounts: data.stats.total_accounts || 0,
        });
      }
    } catch (error) {
      // API stats are optional (FastAPI may not be deployed); leave the tile hidden.
      console.error('Error fetching API stats:', error);
    }
  };

  // Action items first; highlighted in brand yellow only when something is waiting.
  const statCards = [
    {
      label: 'Awaiting Approval',
      value: stats.pendingApprovals,
      sub: stats.pendingApprovals > 0 ? 'Review and send to Progress' : 'Nothing waiting',
      icon: ClipboardCheck,
      path: '/admin/approvals',
      needsAction: stats.pendingApprovals > 0,
    },
    {
      label: 'RFQs to Quote',
      value: stats.rfqsToQuote,
      sub: stats.rfqsToQuote > 0 ? 'Submitted or under review' : 'Nothing waiting',
      icon: FileQuestion,
      path: '/admin/inquiries',
      needsAction: stats.rfqsToQuote > 0,
    },
    {
      label: 'On Hold',
      value: stats.onHold,
      sub: 'Orders held in Progress',
      icon: PauseCircle,
      path: '/admin/orders',
    },
    {
      label: 'Orders This Week',
      value: stats.thisWeekOrders,
      sub: `${stats.lastWeekOrders} last week`,
      icon: CalendarDays,
      path: '/admin/orders',
      trend: stats.thisWeekOrders - stats.lastWeekOrders,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard"
        description="What needs attention across the portal today."
        actions={
          <>
            <ChangelogWidget onClick={() => setShowChangelog(true)} />
            <LiveIndicator connected={realtimeConnected} />
            <Button
              variant="secondary"
              size="sm"
              onClick={() => refresh()}
              disabled={loading}
              icon={<RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />}
            >
              Refresh
            </Button>
          </>
        }
      />

      <StatCardGrid cards={statCards} loading={loading} />

      {/* Approval queue — only when there's something in it */}
      {stats.pendingApprovals > 0 && (
        <PendingOrdersWidget maxOrders={5} linkPath="/admin/approvals" />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RecentOrdersCard
          orders={recentOrders}
          loading={loading}
          viewAllPath="/admin/orders"
          orderPath={(order) => `/admin/orders?search=${encodeURIComponent(order.order_number)}`}
        />
        <OrdersChartCard statusCounts={statusCounts} timeline={timeline} loading={loading} />
      </div>

      {/* Backorders & search */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <BackorderManagementCard />
        <SearchAnalyticsWidget onViewFullAnalytics={() => history.push('/admin/search-analytics')} />
      </div>

      {/* System health */}
      <section aria-labelledby="system-health" className="space-y-4 pt-2">
        <div className="flex items-center justify-between border-t border-gray-200 pt-6">
          <h3 id="system-health" className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
            System health
          </h3>
          <button
            onClick={() => history.push('/admin/queue-monitor')}
            className="text-sm font-medium text-gray-600 hover:text-gray-900 flex items-center gap-1"
          >
            Task queue <ArrowRight className="w-3 h-3" aria-hidden="true" />
          </button>
        </div>

        <OrderSyncButton />
        <JobHistory />

        {apiStats && (
          <Card padding="sm">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-md bg-gray-100 text-gray-500 flex items-center justify-center">
                  <Key className="w-4 h-4" aria-hidden="true" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-gray-900">API integration</p>
                  <p className="text-xs text-gray-500">
                    <span className="tabular-nums">{apiStats.activeApiKeys}</span> active of{' '}
                    <span className="tabular-nums">{apiStats.totalApiKeys}</span> keys across{' '}
                    <span className="tabular-nums">{apiStats.totalAccounts}</span> accounts
                  </p>
                </div>
              </div>
              <a
                href="/api/v1/docs"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-gray-600 hover:text-gray-900 flex items-center gap-1"
              >
                API docs <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
              </a>
            </div>
          </Card>
        )}
      </section>

      <ChangelogModal isOpen={showChangelog} onClose={() => setShowChangelog(false)} />
    </div>
  );
};

export default AdminDashboard;
